import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Brand, EffectiveBrand, fetchEffectiveBrand, logoSrc } from '../api/brandingApi';
import { useAuth } from './AuthContext';

/** White-labelling (SRS #38): the effective brand for the signed-in workspace, applied to the app shell.
 *  The primary colour becomes the design-system accent (--ds-brand-blue and its shades) so every tokenised
 *  screen follows it; the logo and name replace the platform's in the sidebar. Signed-out pages keep the
 *  platform brand. */
export interface BrandingContextValue {
  brand: EffectiveBrand | null;
  logo: string | null;          // absolute URL or null → platform logo file
  refresh: () => Promise<void>;
}

export const PLATFORM: Brand = {
  source: 'default', owner_user_id: null, brand_name: 'Policyora', tagline: 'Global Policy Intelligence',
  primary_color: '#444ce7', accent_color: '#3538cd', logo_url: null, watermark_enabled: true, watermark_text: 'CONFIDENTIAL',
  footer_text: '', platform_credit: true, updated_at: null,
};

const VARS = ['--ds-brand-blue', '--ds-brand-blue-bright', '--ds-brand-orange', '--ds-brand-teal'] as const;
const NAVY = '#082a59';

/** Relative luminance of a #rrggbb colour, as WCAG 2.x defines it. */
function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The text colour that reads better on the brand colour: white or the platform navy. */
export function brandInk(primary: string): string {
  if (!/^#[0-9a-f]{6}$/i.test(primary)) return NAVY;
  const l = luminance(primary);
  const onWhite = 1.05 / (l + 0.05);
  const onNavy = (l + 0.05) / (luminance(NAVY) + 0.05);
  return onWhite >= onNavy ? '#ffffff' : NAVY;
}

/** Set (or clear, with null) the accent variables on <html>. Exported for tests. */
export function applyBrandColours(primary: string | null, accent: string | null): void {
  const root = document.documentElement;
  if (!primary) {
    VARS.forEach((v) => root.style.removeProperty(v));
    root.style.removeProperty('--ds-brand-orange-deep');
    root.style.removeProperty('--ds-brand-ink');
    return;
  }
  VARS.forEach((v) => root.style.setProperty(v, primary));
  root.style.setProperty('--ds-brand-orange-deep', accent || primary);
  root.style.setProperty('--ds-brand-ink', brandInk(primary));
}

export const BrandingContext = createContext<BrandingContextValue | null>(null);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const [brand, setBrand] = useState<EffectiveBrand | null>(null);

  const refresh = useCallback(async () => {
    if (!user) { setBrand(null); applyBrandColours(null, null); return; }
    try {
      const b = await fetchEffectiveBrand();
      setBrand(b);
      applyBrandColours(b.source === 'default' ? null : b.primary_color, b.accent_color);
    } catch { setBrand(null); applyBrandColours(null, null); }
  }, [user]);

  useEffect(() => { if (!loading) { void refresh(); } }, [loading, refresh]);
  useEffect(() => () => applyBrandColours(null, null), []);

  return <BrandingContext.Provider value={{ brand, logo: logoSrc(brand?.logo_url ?? null), refresh }}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => {
  const ctx = useContext(BrandingContext);
  return ctx ?? { brand: null, logo: null, refresh: async () => undefined };
};
